/**
 * The Files browser list view — a TanStack Table of the current directory.
 *
 * Finder semantics: single click selects, double-click a folder navigates into
 * it, double-click a file opens it in the preview pane, double-click a name
 * renames it. Rows are draggable onto a folder row, a breadcrumb, or a sidebar
 * item to move them. A ⋯ button on each row opens the row menu for users who do
 * not right-click.
 *
 * Directories always sort above files for a given sort key, which is the one
 * ordering every file manager a non-technical user has ever used agrees on.
 */

import { useEffect, useMemo, useState, type DragEvent, type KeyboardEvent, type ReactElement } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { type VfsFileEntry } from '../shared/api-client';
import { FileIconComponent, fileIconKindFor } from './FileIconComponent';
import './FileListComponent.css';

/** Formats a byte size the way the rest of the app does. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatModified(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Same compact form Finder uses: "Apr 3, 2026" or "Today, 2:04 PM".
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return `Today, ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

export interface FileListComponentProps {
  entries: VfsFileEntry[];
  /** The set of currently-selected paths. Multi-select: cmd/ctrl-click toggles,
   *  shift-click selects a range from the last anchor. */
  selectedPaths: Set<string>;
  /** Paths of writable mounts — a row is movable/droppable only inside one. */
  writableRoots: string[];
  /** Frozen mirror folder paths — rows inside one refuse move/drop. */
  syncSourcePaths: Set<string>;
  /** A new-folder input row is showing at the top of the list. */
  creatingFolder: boolean;
  pendingFolderName: string;
  onFolderNameChange: (name: string) => void;
  onCommitFolder: () => void;
  onCancelFolder: () => void;
  /** Inline rename, driven by double-clicking a name cell OR by setting
   *  `renameTarget` from the row menu. */
  onRename: (path: string, newName: string) => Promise<boolean>;
  /** When set, the list opens the inline rename input for this path. Cleared by
   *  the list once the rename commits or cancels, via onRenameTargetClear. */
  renameTarget?: string | null;
  onRenameTargetClear?: () => void;
  /** A row click with its modifier state. The page decides plain/cmd/shift
   *  semantics and updates selectedPaths; the list only reports the click. */
  onSelectClick: (entry: VfsFileEntry, e: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean }) => void;
  onOpen: (entry: VfsFileEntry) => void;
  onNavigate: (dir: string) => void;
  /** Move a set of paths into a folder. The list passes every selected path
   *  when the dragged row is part of the selection, so a multi-select drag moves
   *  them all — the gesture people already expect from every file manager. */
  onDropMove: (fromPaths: string[], toDir: string) => void;
  onRowMenu: (entry: VfsFileEntry, anchor: HTMLElement) => void;
  /** Notified on drag start/end so the breadcrumbs and sidebar show drop affordance. */
  onDragStateChange: (active: boolean) => void;
}

/** True when `path` is inside a writable mount and not inside a synced folder. */
function isRowWritable(path: string, writableRoots: string[], syncSourcePaths: Set<string>): boolean {
  if (!writableRoots.some((m) => path === m || path.startsWith(`${m}/`))) return false;
  for (const frozen of syncSourcePaths) {
    if (path === frozen || path.startsWith(`${frozen}/`)) return false;
  }
  return true;
}

export function FileListComponent(props: FileListComponentProps): ReactElement {
  const {
    entries, selectedPaths, writableRoots, syncSourcePaths,
    creatingFolder, pendingFolderName, onFolderNameChange, onCommitFolder, onCancelFolder,
    onRename, renameTarget, onRenameTargetClear, onSelectClick, onOpen, onNavigate, onDropMove, onRowMenu, onDragStateChange,
  } = props;

  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  const [renaming, setRenaming] = useState<{ path: string; value: string } | null>(null);

  // The row menu can request an inline rename for a specific path. Open the
  // input the moment that request arrives, and clear the request so a future
  // menu open does not re-trigger it.
  useEffect(() => {
    if (renameTarget) {
      const entry = entries.find((e) => e.path === renameTarget);
      if (entry) setRenaming({ path: entry.path, value: entry.name });
      onRenameTargetClear?.();
    }
  }, [renameTarget, entries, onRenameTargetClear]);

  // Directories-first is baked into each column's sort function rather than a
  // separate `kind` sort, so re-sorting by Size or Modified still keeps folders
  // on top — the one invariant a file manager must not violate. TanStack's
  // sortingFn receives Row<T> wrappers, so we read .original off each row.
  const dirFirst = <T,>(nameAccessor: (row: VfsFileEntry) => T) =>
    (rowA: { original: VfsFileEntry }, rowB: { original: VfsFileEntry }) => {
      const a = rowA.original;
      const b = rowB.original;
      if (a.kind !== b.kind) return a.kind === 'directory' ? -1 : 1;
      const av = nameAccessor(a);
      const bv = nameAccessor(b);
      return av < bv ? -1 : av > bv ? 1 : 0;
    };

  const columns = useMemo<ColumnDef<VfsFileEntry>[]>(() => [
    {
      id: 'name',
      accessorKey: 'name',
      sortingFn: dirFirst((r) => r.name.toLowerCase()),
      header: 'Name',
      cell: ({ row }) => {
        const entry = row.original;
        const writable = isRowWritable(entry.path, writableRoots, syncSourcePaths);
        if (renaming?.path === entry.path) {
          return (
            <input
              className="file-list-rename"
              autoFocus
              defaultValue={renaming.value}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setRenaming({ path: entry.path, value: e.target.value })}
              onKeyDown={async (e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const ok = await onRename(entry.path, renaming.value);
                  if (ok) setRenaming(null);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setRenaming(null);
                }
              }}
              onBlur={() => setRenaming(null)}
            />
          );
        }
        return (
          <span
            className="file-list-name"
            onDoubleClick={(e) => {
              // Double-clicking a file's name opens an inline rename. A folder
              // name double-click is left to bubble to the row, which navigates
              // into the folder. stopPropagation keeps the row's onDoubleClick
              // (which would open the file in the preview) from also firing.
              if (entry.kind === 'directory') return;
              if (!writable) return;
              e.stopPropagation();
              setRenaming({ path: entry.path, value: entry.name });
            }}
          >
            <FileIconComponent kind={fileIconKindFor(entry.kind, entry.mediaType, entry.name)} size={16} />
            <span className="file-list-name-text">{entry.name}</span>
            {entry.kind === 'directory' && syncSourcePaths.has(entry.path) && (
              <span className="file-list-badge">synced</span>
            )}
          </span>
        );
      },
    },
    {
      id: 'size',
      accessorKey: 'sizeBytes',
      header: 'Size',
      sortingFn: dirFirst((r) => r.sizeBytes),
      cell: ({ row }) => {
        const e = row.original;
        if (e.kind === 'directory') return e.note ?? '—';
        return formatSize(e.sizeBytes);
      },
    },
    {
      id: 'modified',
      accessorFn: (row) => row.updatedAt ?? '',
      header: 'Modified',
      sortingFn: dirFirst((r) => r.updatedAt ?? ''),
      cell: ({ row }) => formatModified(row.original.updatedAt),
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const entry = row.original;
        if (creatingFolder || renaming?.path === entry.path) return null;
        return (
          <button
            type="button"
            className="file-list-row-menu"
            title="More actions"
            aria-label={`Actions for ${entry.name}`}
            onClick={(e) => { e.stopPropagation(); onRowMenu(entry, e.currentTarget); }}
          >
            ⋯
          </button>
        );
      },
    },
  ], [creatingFolder, renaming, syncSourcePaths, writableRoots, onRename, onRowMenu]);

  const table = useReactTable({
    data: entries,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // A drag carries every selected path, so a multi-select drag moves them all.
  // If the user starts dragging a row that is NOT part of the current selection,
  // the drag becomes a single-row move of just that row — the Finder default,
  // so an accidental drag never relocates a whole selection the user forgot.
  const handleRowDragStart = (e: DragEvent<HTMLTableRowElement>, entry: VfsFileEntry) => {
    const dragging = selectedPaths.has(entry.path) && selectedPaths.size > 1
      ? [...selectedPaths]
      : [entry.path];
    // Newline-joined list is the primary payload; the single-path type is kept
    // as a fallback so breadcrumb/sidebar drop handlers that only read one path
    // still work until they migrate.
    e.dataTransfer.setData('text/vfs-paths', dragging.join('\n'));
    e.dataTransfer.setData('text/vfs-path', dragging[0] ?? entry.path);
    // effectAllowed must cover every drop target this drag can land on. The
    // file-manager targets (folder rows, breadcrumbs, sidebar) relocate the
    // file — a 'move' — while the chat composer takes it as a path reference —
    // a 'copy'. 'copyMove' permits both, and the file-manager targets don't
    // read dropEffect so they're unaffected. Read-only files can't be moved,
    // so they stay 'copy'. (We can't use 'link' for the chat case: the composer
    // sets dropEffect, and 'link' is only permitted by effectAllowed values
    // that include 'link', which 'move'/'copy' do not — the browser then
    // rejects the drop and the path never reaches the composer.)
    e.dataTransfer.effectAllowed = isRowWritable(entry.path, writableRoots, syncSourcePaths)
      ? 'copyMove'
      : 'copy';
    onDragStateChange(true);
  };

  const handleFolderDrop = (e: DragEvent<HTMLTableRowElement>, folder: VfsFileEntry) => {
    e.preventDefault();
    e.currentTarget.classList.remove('file-list-row--drag-over');
    // Prefer the multi-path payload, fall back to the single path.
    const raw = e.dataTransfer.getData('text/vfs-paths') || e.dataTransfer.getData('text/vfs-path');
    if (!raw) return;
    const fromPaths = raw.split('\n').filter((p) => p && p !== folder.path && !p.startsWith(`${folder.path}/`));
    if (fromPaths.length > 0) onDropMove(fromPaths, folder.path);
  };

  return (
    <div className="file-list-scroll">
      <table className="file-list">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  className={header.column.getCanSort() ? 'file-list-th sortable' : 'file-list-th'}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getIsSorted() === 'asc' ? ' ↑' : header.column.getIsSorted() === 'desc' ? ' ↓' : ''}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {creatingFolder && (
            <tr className="file-list-row file-list-row--creating">
              <td colSpan={4}>
                <span className="file-list-name">
                  <FileIconComponent kind="folder" size={16} />
                  <input
                    className="file-list-rename"
                    autoFocus
                    placeholder="Folder name"
                    value={pendingFolderName}
                    onChange={(e) => onFolderNameChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); onCommitFolder(); }
                      else if (e.key === 'Escape') { e.preventDefault(); onCancelFolder(); }
                    }}
                    onBlur={() => { if (pendingFolderName.trim()) onCommitFolder(); else onCancelFolder(); }}
                  />
                </span>
              </td>
            </tr>
          )}
          {table.getRowModel().rows.map((row) => {
            const entry = row.original;
            const isDir = entry.kind === 'directory';
            const droppable = isDir && isRowWritable(entry.path, writableRoots, syncSourcePaths);
            return (
              <tr
                key={row.id}
                className={`file-list-row${selectedPaths.has(entry.path) ? ' file-list-row--selected' : ''}`}
                draggable={entry.kind === 'file' || isRowWritable(entry.path, writableRoots, syncSourcePaths)}
                onDragStart={(e) => handleRowDragStart(e, entry)}
                onDragEnd={() => onDragStateChange(false)}
                onDragOver={(e) => { if (droppable && (e.dataTransfer.types.includes('text/vfs-paths') || e.dataTransfer.types.includes('text/vfs-path'))) e.preventDefault(); }}
                onDragEnter={(e) => { if (droppable) e.currentTarget.classList.add('file-list-row--drag-over'); }}
                onDragLeave={(e) => e.currentTarget.classList.remove('file-list-row--drag-over')}
                onDrop={(e) => droppable && handleFolderDrop(e, entry)}
                onClick={(e) => { if (e.detail === 2) return; onSelectClick(entry, e); }}
                onDoubleClick={() => (isDir ? onNavigate(entry.path) : onOpen(entry))}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            );
          })}
          {entries.length === 0 && !creatingFolder && (
            <tr className="file-list-empty">
              <td colSpan={4}>This folder is empty — drag files in or use Upload.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
